import { bus } from "./runtime";
import { safeStorage } from "./safe-storage";

/**
 * Shared page audio — one lazy AudioContext with automatic unlock and
 * output-device (sink) routing.
 *
 *   const ctx = granrojo.audio.context();   // build your WebAudio graph on it
 *
 * Unlock: browsers keep an AudioContext suspended until a user gesture.
 * The first pointer/key interaction anywhere on the page resumes the
 * shared context, so pages don't need per-handler `resume()` calls.
 * Truly gestureless kiosks should launch Chrome with
 * `--autoplay-policy=no-user-gesture-required`.
 *
 * Sink routing: `setSink(deviceId)` uses AudioContext.setSinkId()
 * (Chromium) and persists per page path, so an installation machine
 * keeps its own speakers. The bus carries `$audio/state`.
 *
 * The bus carries control messages only — audio samples never touch it.
 */

export type AudioState = "idle" | "suspended" | "running" | "closed";

export interface AudioStatus {
  state: AudioState;
  /** Active output device id; "" = system default. */
  sinkId: string;
}

export interface AudioOutputDevice {
  deviceId: string;
  label: string;
}

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function sinkKey(pagePath: string): string {
  return `granrojo:audio-sink:${pagePath}`;
}

export function loadSink(storage: StorageLike, key: string): string | null {
  try {
    const raw = storage.getItem(key);
    return raw && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

export function saveSink(storage: StorageLike, key: string, deviceId: string): void {
  try {
    if (deviceId) storage.setItem(key, deviceId);
    else storage.removeItem(key);
  } catch {
    // Storage full / disabled — the sink still applies for this session.
  }
}

/** srcdoc-safe page path, same convention as gr-connect overrides. */
function pagePath(): string {
  try {
    return new URL(document.baseURI).pathname;
  } catch {
    return "unknown";
  }
}

type SinkCapableContext = AudioContext & { setSinkId?: (sinkId: string) => Promise<void> };

let ctx: AudioContext | null = null;
let sinkId = "";
let unlockArmed = false;
const listeners = new Set<(status: AudioStatus) => void>();

function status(): AudioStatus {
  return { state: ctx ? (ctx.state as AudioState) : "idle", sinkId };
}

function notify(): void {
  const current = status();
  bus.publish("$audio/state", current.state, { src: "audio" });
  for (const listener of listeners) listener(current);
}

/** Resume on the next interaction. Re-armed if the context suspends again. */
function armUnlock(): void {
  if (unlockArmed) return;
  unlockArmed = true;
  const unlock = (): void => {
    unlockArmed = false;
    document.removeEventListener("pointerdown", unlock, true);
    document.removeEventListener("keydown", unlock, true);
    void ctx?.resume().catch(() => {});
  };
  document.addEventListener("pointerdown", unlock, { capture: true, once: true });
  document.addEventListener("keydown", unlock, { capture: true, once: true });
}

async function applySink(deviceId: string): Promise<void> {
  const context = audio.context() as SinkCapableContext;
  if (typeof context.setSinkId !== "function") {
    throw new Error("AudioContext.setSinkId is not supported in this browser");
  }
  await context.setSinkId(deviceId);
  sinkId = deviceId;
  notify();
}

export const audio = {
  /** The page's shared AudioContext — created lazily, unlocked automatically. */
  context(): AudioContext {
    if (!ctx) {
      ctx = new AudioContext();
      ctx.addEventListener("statechange", () => {
        if (ctx?.state === "suspended") armUnlock();
        notify();
      });
      if (ctx.state === "suspended") armUnlock();
      const stored = loadSink(safeStorage(), sinkKey(pagePath()));
      if (stored) void applySink(stored).catch(() => saveSink(safeStorage(), sinkKey(pagePath()), ""));
      notify();
    }
    return ctx;
  },

  status,

  /** Route output to a device (Chromium). "" reverts to the system default. Persists per page. */
  async setSink(deviceId: string): Promise<void> {
    await applySink(deviceId);
    saveSink(safeStorage(), sinkKey(pagePath()), deviceId);
  },

  /** Available audio outputs. Labels may be blank until a media permission is granted. */
  async outputs(): Promise<AudioOutputDevice[]> {
    if (!navigator.mediaDevices?.enumerateDevices) return [];
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((device) => device.kind === "audiooutput")
      .map((device, i) => ({
        deviceId: device.deviceId,
        label:
          device.label ||
          (device.deviceId === "default" ? "System default" : `Output ${i + 1}`),
      }));
  },

  subscribe(listener: (status: AudioStatus) => void): () => void {
    listeners.add(listener);
    listener(status());
    return () => {
      listeners.delete(listener);
    };
  },
};
